import { useEffect, useState } from "react";
import { graphql } from "react-relay";
import { fetchQuery } from "relay-runtime";
import { RelayEnvironment } from "@/RelayEnvironment";
import type { hospitalSelectorQuery$data as QueryType } from './__generated__/hospitalSelectorQuery.graphql'
import specialtyDefines from "@/defines/specialtyDefines";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const HospitalSelectorQuery = graphql`
  query hospitalSelectorQuery(
        $after: String
        $first: Int
        $name: String
    )
  {
    medical_center_connection(
        where: {name: {_like: $name}},
        first: $first,
        after: $after
    ) {
        edges {
            node {
                id
                name
                city
                address
                created_at
                updated_at
                textlink
                district
                telephone
                medical_center_specialties {
                    specialty {
                        name
                        id
                    }
                }
            }
            cursor
        }
        pageInfo {
            endCursor
            hasNextPage
        }
    }
  }
`;

export interface HospitalSelectorNodeType {
    address: string | null | undefined;
    city: string | null | undefined;
    created_at: any | null | undefined;
    district: string | null | undefined;
    id: string;
    medical_center_specialties: ReadonlyArray<{
        specialty: {
            id: string;
            name: string;
        };
    }>;
    name: string | null | undefined;
    telephone: string | null | undefined;
    textlink: string | null | undefined;
    updated_at: any | null | undefined;
}

export enum hospitalSelectorType {
    Anesth,
    MedicalCenterContentManager,
    InsuranceAgent
}

export default function HospitalSelector(props: { openModal: boolean, confirmHospital: (data: HospitalSelectorNodeType) => void, type: hospitalSelectorType, closeModal?: () => void }) {
    const [inputValue, setInputValue] = useState("");
    const [data, setData] = useState<QueryType>();
    const [nodeData, setNodeData] = useState<HospitalSelectorNodeType[]>([] as HospitalSelectorNodeType[]);
    const [endCursor, setEndCursor] = useState("");
    const [selectedHospital, setSelectedHospital] = useState<HospitalSelectorNodeType | null>(null);


    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    useEffect(() => {
        setNodeData([]);
        if (inputValue) {
            loadData();
        } else {
            setData(undefined);
        }
    }, [inputValue]);

    useEffect(() => {
        setEndCursor(data?.medical_center_connection.pageInfo.endCursor ?? "");
    }, [data]);

    useEffect(() => {
        if (selectedHospital) {
            setNodeData([]);
            console.log(selectedHospital);
        }
    }, [selectedHospital]);


    const loadMore = () => {
        if (endCursor) {
            fetchQuery(RelayEnvironment, HospitalSelectorQuery, { after: endCursor, name: `%${inputValue}%`, first: 10 })
                .subscribe({
                    next: (response) => {
                        var newData = response as QueryType;
                        newData.medical_center_connection.edges.forEach((item) => {
                            var forAddData = {} as HospitalSelectorNodeType;
                            forAddData.address = item.node.address;
                            forAddData.city = item.node.city;
                            forAddData.created_at = item.node.created_at;
                            forAddData.district = item.node.district;
                            forAddData.id = item.node.id;
                            forAddData.medical_center_specialties = item.node.medical_center_specialties;
                            forAddData.name = item.node.name;
                            forAddData.telephone = item.node.telephone;
                            forAddData.textlink = item.node.textlink;
                            forAddData.updated_at = item.node.updated_at;
                            nodeData.push(forAddData);
                        });
                        setNodeData(nodeData);
                        setData(newData);
                    },
                    error: (err: any) => {
                        console.log('err');
                        console.log(err);
                    },
                });
        }
    };

    const resetHospital = () => {
        setNodeData([]);
        setSelectedHospital(null);
        loadData();
    }
    const loadData = () => {
        fetchQuery(RelayEnvironment, HospitalSelectorQuery, { name: `%${inputValue}%`, first: 10 })
            .subscribe({
                next: (response) => {
                    var newData = response as QueryType;
                    setData(newData);
                    newData.medical_center_connection.edges.forEach((item) => {
                        var forAddData = {} as HospitalSelectorNodeType;
                        forAddData.address = item.node.address;
                        forAddData.city = item.node.city;
                        forAddData.created_at = item.node.created_at;
                        forAddData.district = item.node.district;
                        forAddData.id = item.node.id;
                        forAddData.medical_center_specialties = item.node.medical_center_specialties;
                        forAddData.name = item.node.name;
                        forAddData.telephone = item.node.telephone;
                        forAddData.textlink = item.node.textlink;
                        forAddData.updated_at = item.node.updated_at;
                        nodeData.push(forAddData);
                    });
                    setNodeData(nodeData);
                },
                error: (err: any) => {
                    console.log('err');
                    console.log(err);
                },
            });
    };

    const confirmSelect = () => {
        if (selectedHospital) {
            props.confirmHospital(selectedHospital);
            resetHospital();
        }
    };

    return (
        <>
            <Dialog open={props.openModal} onClose={() => { }} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity 
                    data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 
                    data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            style={{ width: '80vw', maxHeight: '80vh', overflowY: 'auto' }}
                            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all  
                            data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out 
                            data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <ExclamationTriangleIcon aria-hidden="true" className="h-6 w-6 text-red-600" />
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                        {props.type === hospitalSelectorType.Anesth || props.type === hospitalSelectorType.MedicalCenterContentManager
                                            ? "請選擇您所服務之醫院" : "請選擇醫療院所"}
                                    </DialogTitle>



                                    {
                                        selectedHospital ?
                                            <article
                                                key={selectedHospital!.id}
                                                className="relative flex flex-col gap-8 py-5 isolate lg:flex-row"
                                            >
                                                <div className="relative aspect-[16/9] sm:aspect-[2/1] lg:aspect-[16/9] lg:w-60 lg:shrink-0">
                                                    <img
                                                        alt="Hospital Image"
                                                        src="https://images.pexels.com/photos/668298/pexels-photo-668298.jpeg" // todo : selectedHospital!.image
                                                        className="absolute inset-0 object-cover w-full h-full rounded-2xl bg-gray-50"
                                                    />
                                                    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center text-xs gap-x-4">
                                                        <a className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100">
                                                            {selectedHospital!.district
                                                                ? selectedHospital!.city + ' ' + selectedHospital!.district
                                                                : selectedHospital!.city}
                                                        </a>
                                                    </div>
                                                    <div className="relative max-w-xl group">
                                                        <h3 className="mt-3 text-2xl font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                                                            {selectedHospital!.textlink ? (
                                                                <a href={selectedHospital!.textlink}>
                                                                    <span className="absolute inset-0" />
                                                                    {selectedHospital!.name}
                                                                </a>
                                                            ) : (
                                                                <a>
                                                                    <span className="absolute inset-0" />
                                                                    {selectedHospital!.name}
                                                                </a>
                                                            )}
                                                        </h3>

                                                        <p className="mt-2 text-base leading-6 text-gray-600">
                                                            專長 :{' '}
                                                        </p>

                                                        <ul className="list-disc list-inside">
                                                            {
                                                                selectedHospital!.medical_center_specialties.slice(0, 3).map((specialty: any) => (
                                                                    <li key={specialty.specialty.id} className="mt-1 text-sm">
                                                                        {specialtyDefines.find(
                                                                            (e) => e.enMap === specialty.specialty.name.toLowerCase()
                                                                        )?.name ?? specialty.specialty.name}
                                                                    </li>
                                                                ))
                                                            }
                                                            {
                                                                selectedHospital!.medical_center_specialties.length < 3 ? <></> : (
                                                                    <li key='more' className="mt-1 text-sm">
                                                                        更多...
                                                                    </li>
                                                                )
                                                            }
                                                        </ul>

                                                        <p className="mt-2 text-sm leading-6 text-gray-600">
                                                            地址 : {selectedHospital!.address}
                                                        </p>
                                                        {selectedHospital!.textlink ? (
                                                            <p className="mt-2 text-sm leading-6 text-gray-600">
                                                                {selectedHospital!.textlink}
                                                            </p>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {selectedHospital!.telephone ? (
                                                            <p className="mt-2 text-sm text-gray-600">
                                                                電話 : {selectedHospital!.telephone}
                                                            </p>
                                                        ) : (
                                                            <></>
                                                        )}

                                                    </div>
                                                </div>
                                            </article>

                                            :

                                            <div>
                                                <h1>
                                                    {props.type === hospitalSelectorType.Anesth || props.type === hospitalSelectorType.MedicalCenterContentManager ?
                                                        "歡迎使用 ! 請選擇您所服務之醫院" : "歡迎使用 ! 請選擇醫療院所"}</h1>
                                                <input value={inputValue} onChange={handleChange} />
                                                {
                                                    nodeData.map((data: HospitalSelectorNodeType) => (
                                                        <article
                                                            key={data.id}
                                                            className="relative flex flex-col gap-8 py-5 isolate lg:flex-row"
                                                        >
                                                            <div className="relative aspect-[16/9] sm:aspect-[2/1] lg:aspect-[16/9] lg:w-60 lg:shrink-0">
                                                                <img
                                                                    alt="Hospital Image"
                                                                    src="https://images.pexels.com/photos/668298/pexels-photo-668298.jpeg" // todo : data.image
                                                                    className="absolute inset-0 object-cover w-full h-full rounded-2xl bg-gray-50"
                                                                />
                                                                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center text-xs gap-x-4">
                                                                    <a className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100">
                                                                        {data.district
                                                                            ? data.city + ' ' + data.district
                                                                            : data.city}
                                                                    </a>
                                                                </div>
                                                                <div className="relative max-w-xl group">
                                                                    <h3 className="mt-3 text-2xl font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                                                                        {data.textlink ? (
                                                                            <a href={data.textlink}>
                                                                                <span className="absolute inset-0" />
                                                                                {data.name}
                                                                            </a>
                                                                        ) : (
                                                                            <a>
                                                                                <span className="absolute inset-0" />
                                                                                {data.name}
                                                                            </a>
                                                                        )}
                                                                    </h3>

                                                                    <p className="mt-2 text-base leading-6 text-gray-600">
                                                                        專長 :{' '}
                                                                    </p>

                                                                    <ul className="list-disc list-inside">
                                                                        {
                                                                            data.medical_center_specialties.slice(0, 3).map((specialty: any) => (
                                                                                <li key={specialty.specialty.id} className="mt-1 text-sm">
                                                                                    {specialtyDefines.find(
                                                                                        (e) => e.enMap === specialty.specialty.name.toLowerCase()
                                                                                    )?.name ?? specialty.specialty.name}
                                                                                </li>
                                                                            ))
                                                                        }
                                                                        {
                                                                            data.medical_center_specialties.length < 3 ? <></> : (
                                                                                <li key='more' className="mt-1 text-sm">
                                                                                    更多...
                                                                                </li>
                                                                            )
                                                                        }
                                                                    </ul>

                                                                    <p className="mt-2 text-sm leading-6 text-gray-600">
                                                                        地址 : {data.address}
                                                                    </p>
                                                                    {data.textlink ? (
                                                                        <p className="mt-2 text-sm leading-6 text-gray-600">
                                                                            {data.textlink}
                                                                        </p>
                                                                    ) : (
                                                                        <></>
                                                                    )}
                                                                    {data.telephone ? (
                                                                        <p className="mt-2 text-sm text-gray-600">
                                                                            電話 : {data.telephone}
                                                                        </p>
                                                                    ) : (
                                                                        <></>
                                                                    )}

                                                                </div>
                                                                <button
                                                                    onClick={() => { setSelectedHospital(data) }}
                                                                    className="flex w-500 justify-center rounded-md bg-red-400 px-3 py-1.5 mt-3
                                                                        text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-300
                                                                        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 
                                                                        focus-visible:outline-red-400"
                                                                >
                                                                    {props.type === hospitalSelectorType.Anesth || props.type === hospitalSelectorType.MedicalCenterContentManager
                                                                        ? '這是我的醫院' : '選擇'}
                                                                </button>
                                                            </div>
                                                        </article>

                                                    ))
                                                }
                                                {
                                                    (!data || !data.medical_center_connection.pageInfo) ? '' :
                                                        data.medical_center_connection.pageInfo.hasNextPage && (
                                                            <button
                                                                type="button"
                                                                onClick={loadMore}
                                                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                            >
                                                                顯示更多
                                                            </button>
                                                        )
                                                }
                                            </div>
                                    }
                                </div>
                            </div>
                            {
                                selectedHospital ?
                                    <>
                                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                onClick={confirmSelect}
                                                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                            >
                                                確認
                                            </button>
                                            <button
                                                type="button"
                                                onClick={resetHospital}
                                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                            >
                                                重新選擇
                                            </button>

                                        </div>
                                        {
                                            props.type === hospitalSelectorType.Anesth || props.type === hospitalSelectorType.MedicalCenterContentManager ?
                                                <div className="text-right">
                                                    <p className="text-sm">我確定這個就是我的醫療院所，選擇後不可進行更改 ! </p>
                                                </div>
                                                :
                                                <></>

                                        }
                                    </>
                                    :
                                    <>
                                        {
                                            props.type === hospitalSelectorType.Anesth || props.type === hospitalSelectorType.InsuranceAgent ?

                                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                                    <button
                                                        type="button"
                                                        onClick={props.closeModal}
                                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-red-400 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-300 sm:mt-0 sm:w-auto"
                                                    >
                                                        返回
                                                    </button>
                                                </div>

                                                : <></>
                                        }
                                    </>
                            }
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    );
}